/**
 * ChecklistItemContextMenu — right-click action picker for a checklist item.
 *
 * Stack 12 MR-B (qf!TBD) — wires the FE side of the tree-write
 * endpoints landed in qb!2962:
 *
 *   POST /api/kanban/checklist-items/<id>/move/   {parent_id, position}
 *   POST /api/kanban/checklist-items/<id>/detach/
 *
 * Actions (in surface order):
 *
 *   • Indent under previous sibling      — shortcut for "Make sub-item of
 *                                          the row above this one". Only
 *                                          shown when a previous sibling
 *                                          exists AT THE SAME DEPTH.
 *   • Outdent (promote to parent's level) — only shown when depth > 0.
 *   • Make sub-item of…                  — submenu listing siblings the
 *                                          item could nest under (same
 *                                          checklist, depth + 1 ≤ 5,
 *                                          not the item itself or any
 *                                          descendant).
 *   • Promote to top level               — only shown when depth > 0.
 *   • Nest selected items here           — visible iff `multiSelectCount > 1`
 *                                          AND this item is one of the
 *                                          selected items' permitted parents.
 *   • Delete                             — two-step confirm (mirrors
 *                                          `KanbanContextMenu`'s pattern).
 *
 * This menu is intentionally narrow: NO duplicate, NO move-to-checklist
 * (Stack 12 Q4 — cross-checklist moves require an explicit cut+paste in
 * v1, not a drag or menu action), NO copy, NO archive (checklist items
 * are never archived — they're deleted).
 */

import { useMemo, useRef, useState, type CSSProperties } from 'react'
import { useCloseOnOutsidePointer } from '../../hooks/useCloseOnOutsidePointer'
import { createPortal } from 'react-dom'
import {
  CornerDownRight as IndentIcon,
  ArrowUpFromLine as OutdentIcon,
  Layers as NestIcon,
  CornerUpLeft as PromoteIcon,
  ArrowUpRight as PromoteCardIcon,
  Trash2,
} from 'lucide-react'

import type { CardChecklistItemT } from '../types'
import { useViewportClampedMenuPosition } from '../hooks/useViewportClampedMenuPosition'
import { Z_LAYER } from '../zLayers'

export interface ChecklistItemContextMenuProps {
  /** The item the operator right-clicked on. */
  item: CardChecklistItemT
  /** Every item in the SAME checklist (flat list, server order). The
   *  menu derives siblings + descendant exclusions from this. */
  siblings: CardChecklistItemT[]
  /** Anchor coords from the right-click event. */
  anchor: { x: number; y: number }
  /** When > 1, the operator has multi-selected and the "Nest selected
   *  here" action becomes visible. */
  multiSelectCount?: number
  /** IDs of the multi-selected items (so we can disable "Nest here"
   *  on `item` if `item` is one of them — would create a cycle). */
  multiSelectIds?: Set<string>
  /** Fired when the operator picks Indent / Outdent / Nest /
   *  Promote / Delete. The parent decides how to invoke `api.move…`
   *  + invalidate queries. */
  onIndent?: () => void | Promise<void>
  onOutdent?: () => void | Promise<void>
  onNestUnder?: (parentId: string) => void | Promise<void>
  onPromoteToTop?: () => void | Promise<void>
  onNestSelectedHere?: (parentId: string) => void | Promise<void>
  /** STACK13-MRD — promote this checklist item into a sub-card under
   *  its containing card, in the same lane. The source item is
   *  hard-deleted on success. Menu entry is hidden when the prop is
   *  undefined (host opted out) so read-only embeds don't surface a
   *  destructive action. */
  onPromoteToCard?: () => void | Promise<void>
  onDelete?: () => void | Promise<void>
  onClose: () => void
}

/** Hard cap matches `CheckConstraint(depth__lte=5)` on the model. */
const MAX_DEPTH = 5

export function ChecklistItemContextMenu({
  item,
  siblings,
  anchor,
  multiSelectCount = 0,
  multiSelectIds,
  onIndent,
  onOutdent,
  onNestUnder,
  onPromoteToTop,
  onNestSelectedHere,
  onPromoteToCard,
  onDelete,
  onClose,
}: ChecklistItemContextMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [nestSubmenuOpen, setNestSubmenuOpen] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [nestFilter, setNestFilter] = useState('')
  // UAT P1 menu sweep — shared viewport clamp (ResizeObserver-based,
  // so the nest-submenu / confirm-delete expands re-clamp without a
  // dep-array enumeration). Same math as every other kanban menu.
  const pos = useViewportClampedMenuPosition({ anchor, menuRef })

  // Click-away + Esc close. Identical wiring to KanbanContextMenu so
  // the gesture vocabulary stays consistent across the two surfaces.
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(true, onClose, [menuRef])

  // The set of items the right-clicked item CANNOT nest under: itself
  // and every descendant. We use the materialised `path` to compute
  // this cheaply — any item whose `path` starts with `item.path + "/"`
  // is a descendant.
  const forbiddenParents = useMemo(() => {
    const forbidden = new Set<string>([item.id])
    const itemPath = item.path || item.id
    for (const sib of siblings) {
      const sibPath = sib.path || sib.id
      if (sibPath.startsWith(itemPath + '/')) forbidden.add(sib.id)
    }
    return forbidden
  }, [item, siblings])

  // Candidate parents = same-checklist siblings that aren't forbidden
  // AND wouldn't push depth past the cap. Filtered by the textbox so
  // operators with many items can search.
  const candidateParents = useMemo(() => {
    const q = nestFilter.trim().toLowerCase()
    return siblings.filter((s) => {
      if (forbiddenParents.has(s.id)) return false
      // Server enforces this too; pre-filter for instant UX feedback.
      if ((s.depth ?? 0) + 1 > MAX_DEPTH) return false
      if (!q) return true
      return s.title.toLowerCase().includes(q)
    })
  }, [siblings, forbiddenParents, nestFilter])

  // Previous-sibling-at-same-depth lookup for the Indent shortcut.
  const previousSiblingId = useMemo(() => {
    const sameDepth = siblings.filter(
      (s) => s.parent === item.parent && s.id !== item.id,
    )
    const sorted = [...sameDepth].sort((a, b) => a.position - b.position)
    let prev: CardChecklistItemT | null = null
    for (const s of sorted) {
      if (s.position < item.position) prev = s
      else break
    }
    return prev?.id ?? null
  }, [siblings, item])

  // When `item` is itself in the multi-select set, "Nest selected
  // here" would create a cycle — disable.
  const canNestSelectedHere =
    multiSelectCount > 1 &&
    onNestSelectedHere !== undefined &&
    !(multiSelectIds?.has(item.id) ?? false)

  const showIndent =
    onIndent !== undefined &&
    previousSiblingId !== null &&
    (item.depth ?? 0) + 1 <= MAX_DEPTH

  const showOutdent =
    onOutdent !== undefined && (item.depth ?? 0) > 0

  const showPromoteToTop =
    onPromoteToTop !== undefined && (item.depth ?? 0) > 0

  const containerStyle: CSSProperties = {
    position: 'fixed',
    left: pos.left,
    top: pos.top,
    // Token, not a literal: the checklist lives INSIDE the card
    // drawer (z-60), so the old zIndex 50 rendered this menu BEHIND
    // the drawer that spawned it.
    zIndex: Z_LAYER.menu,
  }

  // UAT P1 menu sweep — portal to document.body so `position: fixed`
  // anchors to the real viewport instead of whichever transformed /
  // overflow-clipped ancestor the drawer happens to be animating.
  if (typeof document === 'undefined') return null
  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label="Checklist item actions"
      data-testid="checklist-item-context-menu"
      className="min-w-[220px] rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-lg ring-1 ring-black/5 py-1 text-xs"
      style={containerStyle}
    >
      {showIndent && (
        <button
          type="button"
          role="menuitem"
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-900"
          onClick={async () => {
            await onIndent?.()
            onClose()
          }}
        >
          <IndentIcon className="h-3 w-3 text-neutral-500" />
          Indent under previous item
        </button>
      )}

      {showOutdent && (
        <button
          type="button"
          role="menuitem"
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-900"
          onClick={async () => {
            await onOutdent?.()
            onClose()
          }}
        >
          <OutdentIcon className="h-3 w-3 text-neutral-500" />
          Outdent (move to parent's level)
        </button>
      )}

      {onNestUnder && (
        <div className="border-t border-neutral-100 dark:border-neutral-900 my-0.5" />
      )}

      {onNestUnder && (
        <button
          type="button"
          role="menuitem"
          aria-haspopup="menu"
          aria-expanded={nestSubmenuOpen}
          className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-900"
          onClick={() => setNestSubmenuOpen((v) => !v)}
        >
          <span className="flex items-center gap-2">
            <NestIcon className="h-3 w-3 text-neutral-500" />
            Make sub-item of…
          </span>
          <span className="text-neutral-400">▸</span>
        </button>
      )}

      {nestSubmenuOpen && onNestUnder && (
        <div className="border-t border-neutral-100 dark:border-neutral-900 max-h-64 overflow-y-auto">
          <div className="px-2 py-1">
            <input
              type="text"
              value={nestFilter}
              onChange={(e) => setNestFilter(e.target.value)}
              placeholder="Filter siblings…"
              autoFocus
              className="w-full rounded border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-1.5 py-0.5 text-[11px]"
            />
          </div>
          {candidateParents.length === 0 && (
            <p className="px-3 py-1 text-[11px] text-neutral-400">
              No eligible siblings.
            </p>
          )}
          {candidateParents.map((s) => (
            <button
              key={s.id}
              type="button"
              role="menuitem"
              className="block w-full truncate px-3 py-1 text-left text-[11px] text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-900"
              style={{ paddingLeft: 12 + (s.depth ?? 0) * 10 }}
              onClick={async () => {
                await onNestUnder?.(s.id)
                onClose()
              }}
            >
              {s.title}
            </button>
          ))}
        </div>
      )}

      {showPromoteToTop && (
        <button
          type="button"
          role="menuitem"
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-900"
          onClick={async () => {
            await onPromoteToTop?.()
            onClose()
          }}
        >
          <PromoteIcon className="h-3 w-3 text-neutral-500" />
          Promote to top level
        </button>
      )}

      {/* STACK13-MRD — "Promote to card" creates a new sub-card under
          the containing card, in the same lane. The new card inherits
          the parent's lane + email trail, then the source item is
          deleted. Distinct from "Promote to top level" (which only
          flattens the item within its checklist). */}
      {onPromoteToCard && (
        <>
          <div className="border-t border-neutral-100 dark:border-neutral-900 my-0.5" />
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
            onClick={async () => {
              await onPromoteToCard?.()
              onClose()
            }}
          >
            <PromoteCardIcon className="h-3 w-3" />
            Promote to card
          </button>
        </>
      )}

      {canNestSelectedHere && (
        <>
          <div className="border-t border-neutral-100 dark:border-neutral-900 my-0.5" />
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
            onClick={async () => {
              await onNestSelectedHere?.(item.id)
              onClose()
            }}
          >
            <NestIcon className="h-3 w-3" />
            Nest {multiSelectCount} selected item{multiSelectCount === 1 ? '' : 's'} here
          </button>
        </>
      )}

      {onDelete && (
        <>
          <div className="border-t border-neutral-100 dark:border-neutral-900 my-0.5" />
          {!confirmingDelete ? (
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
              onClick={() => setConfirmingDelete(true)}
            >
              <Trash2 className="h-3 w-3" />
              Delete item…
            </button>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1.5">
              <button
                type="button"
                onClick={async () => {
                  await onDelete?.()
                  onClose()
                }}
                className="rounded bg-red-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-red-700"
              >
                Confirm delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="rounded px-2 py-0.5 text-[11px] text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-900"
              >
                Cancel
              </button>
            </div>
          )}
        </>
      )}
    </div>,
    document.body,
  )
}
